import React, {CSSProperties, useRef} from 'react';
import '../../styles/components/dashboard/breakdown-section.scss';
import {FontIcon, Icon} from "../common/FontIcon";
import useIntersectionObserver from "../../services/intersection/intersection";

interface IBreakdownSection {
  icon: Icon,
  barLabel: string,
  totalValue: number,
  barValue: number,
  barValueLabel?: string,
  barValueUnit?: string,
  barSubValueLabel?: string,
  barSubValueUnit?: string,
}

export default function BreakdownSection({icon, barLabel, totalValue, barValue, barValueUnit, barValueLabel, barSubValueLabel, barSubValueUnit}: IBreakdownSection) {
  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, {freezeOnceVisible: true});
  const isVisible = !!entry?.isIntersecting;
  const barAnimClass = animateBar(isVisible);

  const computedBarStyles = calculateBarStyles(totalValue, barValue);
  const barStyles: CSSProperties = {
    width: `${computedBarStyles?.barWidth}%`,
    content: `${computedBarStyles?.content ?? ``}`,
  }

  const computedPercentageStyle = calculatePercentageStyle(totalValue, barValue);
  const percentageStyles: CSSProperties = {
    color: `${computedPercentageStyle?.color}`,
    textShadow: `${computedPercentageStyle?.textShadow ?? ``}`,
    paddingLeft: `${computedPercentageStyle?.paddingLeft}`,
  }

  const computedBarOpacity = calculateBarOpacity(totalValue, barValue);
  const barOpacity: CSSProperties = {
    backgroundColor: `rgb(107 51 239 / ${computedBarOpacity})`
  }

  const isMajorValueZero = checkMajorValue(barValueLabel);
  const majorDisplayStyle: CSSProperties = {
    display: `${isMajorValueZero}`,
  }

  return (
    <div className={`breakdown-section`}  style={barStyles} ref={ref}>
      <div className="breakdown-section-header">
        <FontIcon icon={icon}/>
        <p className={`bar-label`}>{barLabel}</p>
      </div>
      <div className={`bar-chart ${barAnimClass}`} style={barOpacity}>
        <p style={percentageStyles} className={`bar-percentage`}>{barStyles.content}<span className={`bar-percentage-symbol`}>%</span></p>
      </div>
      <p className={`bar-value`}>
        <span className={`bar-value-major`} style={majorDisplayStyle}>
          <span className={`bar-value-number-major`}>{barValueLabel ?? 0}</span>
          <span className={`bar-value-unit`}>{barValueUnit ?? ``}&nbsp;</span>
        </span>
        {barSubValueLabel !== undefined ? (
          <>
            {barSubValueLabel}<span className={`bar-value-unit`}>{barSubValueUnit ?? ``}</span>
          </>
        ): null}
      </p>
    </div>
  );

  function calculateBarStyles(totalValue?: number, barValue?: number) {
    if (barValue === undefined || totalValue === undefined || barValue < 0) {
      return {
        barWidth: `0`,
        content: `0`
      }
    }

    const isBarValueZero: boolean = (barValue === 0);
    
    if (isBarValueZero) {
      return {
        barWidth: `0`,
        content: `0`
      }
    }

    const barPercent: number = (barValue / totalValue) * 100;

    if (barPercent < 1) {
      return {
        barWidth: barPercent.toFixed(2),
        content: barPercent.toFixed(2)
      }
    }

    if (barPercent < 10) {
      return {
        barWidth: 10, // Sets a minimum width for display purposes
        content: Math.round(barPercent)
      }
    }

    return {
      barWidth: Math.round(barPercent),
      content: Math.round(barPercent)
    }
  }

  function calculateBarOpacity(totalValue?: number, barValue?: number) {
    if (barValue !== undefined && totalValue !== undefined) {
      if (barValue === 0) {
        return 0;
      }
      else if (barValue / totalValue < .01) {
        return .05;
      } else if (barValue / totalValue < .25) {
        return .25;
      } else if (barValue / totalValue < .34) {
        return .5;
      } else if (barValue / totalValue < .49) {
        return .7;
      } else {
        return 1;
      }
    }
  }

  function calculatePercentageStyle(totalValue: number, barValue: number): Partial<CSSProperties> {
    if (barValue === 0 || totalValue === 0) {
      return {
        color: `#777`,
        textShadow: `none`,
        paddingLeft: `0`
      }
    }

    if ((barValue / totalValue * 100) <= 34) {
      return {
        color: `rgb(0 0 0 / 85%)`,
        textShadow: `none`,
        paddingLeft: `0.75em`
      }
    }

    if ((barValue / totalValue * 100) <= 70) {
      return {
        color: `#fff`,
        textShadow: `none`,
        paddingLeft: `0.75em`
      }
    }

    return {
      color: `#fff`,
      textShadow: `0 1px rgb(0 0 0 / 15%)`,
      paddingLeft: `0.75em`
    }
  }

  function checkMajorValue(barValueLabel?: string) {
    if (barValueLabel == undefined) {
      return
    }

    if (barValueLabel === '0') {
      return `none`;
    } else {
      return `inline`;
    }
  }

  function animateBar(isVisible: boolean) {
    if (isVisible) {
      return 'scale-bar-width'
    }
  }
}
